import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Image, Uniter } from "../../../helbest";
import { calculator, Comma, showAllErrors, toFixedNumber } from "../../../base/utils";
import { channelDetails } from "../../../base/env";
import { _Cart_Update } from "../../../store/actions/cart_actions";
import ImgProduct from "../../../assets/images/base/product.png";
import {
  useCheckoutLineDeleteMutation,
  useCheckoutLinesUpdateMutation,
} from "../../../saleor/api";
import { CartItemTransformer } from "../../../services/objectTransformer/cart";
import { set_checkout_id } from "../../../store/actions/login_actions";
import Plum from "./Plum/Plum";
import styles from "./CartItem.module.css";
import { useTranslation } from "react-i18next";

const CartItem = ({ data, checkoutLineId, checkoutRefetch }) => {
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const { Cart, Login } = useSelector((state) => state);
  const dispatch = useDispatch();
  const [checkoutLinesUpdateMutation] = useCheckoutLinesUpdateMutation();
  const [checkoutLineDeleteMutation] = useCheckoutLineDeleteMutation();

  const precision = channelDetails[Login.channel].precision;
  const currency = channelDetails[Login.channel].currency;
  const savingAmount = (data.price - data.payable_amount).toFixed(precision);

  const { id, quantity } = useMemo(() => {
    const order = Cart.find((order) => order.product.id === data.id);
    return {
      id: order?.id || undefined,
      quantity: order?.product?.quantity || 1,
    };
  }, [JSON.stringify(Cart), JSON.stringify(data)]);

  const increment = () => {
    if (!Login.token) {
      const newCart = Cart.map((item) =>
        item.product.id === data.id
          ? { ...item, product: { ...item.product, quantity: item.product.quantity + 1 } }
          : item
      );
      dispatch(_Cart_Update(newCart));
      calculator();
      return;
    }

    setDisabled(true);
    checkoutLinesUpdateMutation({
      variables: {
        id: Login.checkoutId,
        lines: [{ quantity: quantity + 1, lineId: id }],
      },
      onCompleted: (response) => {
        setDisabled(false);
        if (!response.checkoutLinesUpdate.errors.length) {
          dispatch(
            _Cart_Update(
              response.checkoutLinesUpdate.checkout.lines.map((line) =>
                CartItemTransformer(line)
              )
            )
          );
          calculator();
          checkoutRefetch?.();
        } else {
          showAllErrors(response.checkoutLinesUpdate.errors);
        }
      },
    });
  };

  const decrease = () => {
    if (!Login.token) {
      const newCart = Cart.map((item) =>
        item.product.id === data.id
          ? {
            ...item,
            product: { ...item.product, quantity: Math.max(1, item.product.quantity - 1) },
          }
          : item
      );
      dispatch(_Cart_Update(newCart));
      calculator();
      return;
    }

    setDisabled(true);
    checkoutLinesUpdateMutation({
      variables: {
        id: Login.checkoutId,
        lines: [{ quantity: quantity - 1, lineId: checkoutLineId }],
      },
      onCompleted: (response) => {
        setDisabled(false);
        if (!response.checkoutLinesUpdate.errors.length) {
          dispatch(
            _Cart_Update(
              response.checkoutLinesUpdate.checkout.lines.map((line) =>
                CartItemTransformer(line)
              )
            )
          );
          calculator();
          checkoutRefetch?.();
        } else {
          showAllErrors(response.checkoutLinesUpdate.errors);
        }
      },
    });
  };

  const remove_cart_item = () => {
    if (!Login.token) {
      const newCart = Cart.filter((item) => item.product.id !== data.id);
      dispatch(_Cart_Update(newCart));
      calculator();
      return;
    }

    setDisabled(true);
    checkoutLineDeleteMutation({
      variables: {
        id: Login.checkoutId,
        lineId: checkoutLineId,
      },
      onCompleted: (response) => {
        setDisabled(false);
        if (!response.checkoutLineDelete.errors.length) {
          const checkoutLines = response.checkoutLineDelete.checkout?.lines || [];
          const updatedCart = checkoutLines.length
            ? checkoutLines.map((line) => CartItemTransformer(line))
            : Cart.filter((item) => item.product.id !== data.id);
          dispatch(_Cart_Update(updatedCart));
          calculator();
          if (updatedCart.length === 0) {
            dispatch(set_checkout_id({ checkout_id: "" }));
          } else {
            checkoutRefetch?.();
          }
        } else {
          showAllErrors(response.checkoutLineDelete.errors);
        }
      },
    });
  };

  const hasDiscount = data.price !== data.payable_amount;

  return (
    <div className={styles.CartItem}>
      <div className={styles.ProductImageContainer}>
        <Image src={data.image} className={styles.ProductImage} alt={data.id} fallback={ImgProduct} />
      </div>

      <div className={styles.DetailsContainer}>
        <div className={styles.Title}>{data.name}</div>
        <div className={styles.SubTitleContainer}>
          <div className={styles.PricesContainer}>
            <div dir="ltr" className="flex items-center mt-1 gap-2">
              <Uniter childSize={12} unitSize={10} className={hasDiscount ? styles.HasDiscount : ""}>{toFixedNumber(data.price)}</Uniter>
              {hasDiscount && <Uniter childSize={12} unitSize={10} children={data.payable_amount} boldSpan={true} isBold />}
            </div>

            {savingAmount > 0 && (
              <div className={styles.Discount}>
                <span>{t("common.saving")}</span>
                <Uniter className={styles.UnitColor} isBold >{Comma(savingAmount)}</Uniter>
              </div>
            )}

          </div>
          <Plum
            disabled={disabled}
            increment={increment}
            decrement={quantity <= 1 ? remove_cart_item : decrease}
            trash={quantity === 1}
          >
            {quantity}
          </Plum>
        </div>
      </div>
    </div>
  );
};

export default CartItem;